import { FC, FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createResource,
  deleteProductPdf,
  deleteResource,
  getResource,
  listResource,
  updateResource,
  uploadProductPdf,
} from "data/adminApi";
import FileUploader from "components/Admin/FileUploader";

interface SubCategory {
  id: number;
  name: string;
  category_name?: string;
}

interface Spec {
  material: string;
  code: string;
  dimension: any;
  length: string;
  width: string;
}

interface ProductImage {
  id: number;
  image_url: string;
  color: number;
  finish: number;
  color_name?: string;
  finish_name?: string;
}

interface ColorRow {
  id: number;
  name: string;
}
interface FinishRow {
  id: number;
  name: string;
}

const defaultSpec = (): Spec => ({
  material: "",
  code: "",
  dimension: { dimensions: [] },
  length: "0",
  width: "0",
});

const AdminProductForm: FC = () => {
  const { sku } = useParams();
  const isEdit = Boolean(sku);
  const navigate = useNavigate();

  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [colors, setColors] = useState<ColorRow[]>([]);
  const [finishes, setFinishes] = useState<FinishRow[]>([]);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    SKU: "",
    name: "",
    description: "",
    video_url: "",
    finishes: "",
    colors: "",
    status: "None",
    featured: false,
    subcategory: 0,
  });
  const [specs, setSpecs] = useState<Spec[]>([defaultSpec()]);
  const [pdfs, setPdfs] = useState<
    Array<{ slot: number; name: string; url: string }>
  >([]);

  const [imgForm, setImgForm] = useState({
    image_url: "",
    color: 0,
    finish: 0,
  });
  const [newColor, setNewColor] = useState("");
  const [newFinish, setNewFinish] = useState("");

  useEffect(() => {
    Promise.all([
      listResource<SubCategory>("/subcategories/"),
      listResource<ColorRow>("/colors/"),
      listResource<FinishRow>("/finishes/"),
    ]).then(([s, c, f]) => {
      setSubs(s);
      setColors(c);
      setFinishes(f);
    });
  }, []);

  useEffect(() => {
    if (!sku) return;
    getResource<any>(`/products/${encodeURIComponent(sku)}/`).then((p) => {
      setForm({
        SKU: p.SKU,
        name: p.name,
        description: p.description || "",
        video_url: p.video_url || "",
        finishes: p.finishes || "",
        colors: p.colors || "",
        status: p.status || "None",
        featured: !!p.featured,
        subcategory: p.subcategory,
      });
      setSpecs(
        p.specifications?.length
          ? p.specifications.map((s: any) => ({
              material: s.material || "",
              code: s.code || "",
              dimension: s.dimension || { dimensions: [] },
              length: String(s.length ?? "0"),
              width: String(s.width ?? "0"),
            }))
          : [defaultSpec()]
      );
      setImages(p.images || []);
      setPdfs(p.pdf_attachments || []);
    });
  }, [sku]);

  const saveProduct = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.subcategory) {
      setMessage("Select a subcategory.");
      return;
    }
    setLoading(true);
    setMessage("");
    const payload = {
      ...form,
      specifications: specs.map((s) => ({
        ...s,
        length: Number(s.length) || 0,
        width: Number(s.width) || 0,
      })),
    };
    try {
      if (isEdit && sku) {
        await updateResource(
          `/products/${encodeURIComponent(sku)}/`,
          payload
        );
        setMessage("Product updated.");
      } else {
        await createResource("/products/", payload);
        navigate(`/admin/products/${encodeURIComponent(form.SKU)}/edit`);
      }
    } catch (err: any) {
      const detail =
        err?.response?.data?.SKU?.[0] ||
        err?.response?.data?.detail ||
        "Could not save product.";
      setMessage(String(detail));
    } finally {
      setLoading(false);
    }
  };

  const addImage = async () => {
    if (!sku || !imgForm.image_url || !imgForm.color || !imgForm.finish) {
      setMessage("Image URL, color and finish are required.");
      return;
    }
    const product = await getResource<any>(
      `/products/${encodeURIComponent(sku)}/`
    );
    await createResource("/images/", {
      product: product.id,
      image_url: imgForm.image_url,
      color: imgForm.color,
      finish: imgForm.finish,
    });
    const refreshed = await getResource<any>(
      `/products/${encodeURIComponent(sku)}/`
    );
    setImages(refreshed.images || []);
    setImgForm({ image_url: "", color: 0, finish: 0 });
    setMessage("Image added.");
  };

  const removeImage = async (id: number) => {
    await deleteResource(`/images/${id}/`);
    setImages((prev) => prev.filter((i) => i.id !== id));
  };

  const addColor = async () => {
    if (!newColor.trim()) return;
    const c = await createResource<ColorRow>("/colors/", {
      name: newColor.trim(),
    });
    setColors((prev) => [...prev, c]);
    setNewColor("");
  };

  const addFinish = async () => {
    if (!newFinish.trim()) return;
    const f = await createResource<FinishRow>("/finishes/", {
      name: newFinish.trim(),
    });
    setFinishes((prev) => [...prev, f]);
    setNewFinish("");
  };

  const onPdfUpload = async (file: File) => {
    if (!sku) return;
    const updated = await uploadProductPdf(sku, file);
    setPdfs(updated.pdf_attachments || []);
    setMessage("PDF uploaded.");
  };

  return (
    <div className="max-w-4xl">
      <Link
        to="/admin/products"
        className="text-sm text-neutral-500 hover:text-primary-600"
      >
        ← Back to products
      </Link>
      <h1 className="mt-3 text-2xl font-bold text-neutral-900 dark:text-white">
        {isEdit ? `Edit ${sku}` : "New product"}
      </h1>

      {message && (
        <div className="mt-4 rounded-xl bg-primary-50 text-primary-800 text-sm px-4 py-3">
          {message}
        </div>
      )}

      <form
        onSubmit={saveProduct}
        className="mt-6 space-y-4 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            required
            disabled={isEdit}
            placeholder="SKU"
            value={form.SKU}
            onChange={(e) => setForm({ ...form, SKU: e.target.value })}
            className="h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent disabled:opacity-60"
          />
          <input
            required
            placeholder="Product name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <select
            required
            value={form.subcategory || ""}
            onChange={(e) =>
              setForm({ ...form, subcategory: Number(e.target.value) })
            }
            className="h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          >
            <option value="">Subcategory</option>
            {subs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.category_name ? `${s.category_name} / ` : ""}
                {s.name}
              </option>
            ))}
          </select>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          >
            <option value="None">None</option>
            <option value="New">New</option>
            <option value="Hot">Hot</option>
          </select>
          <input
            placeholder="Colors (display text)"
            value={form.colors}
            onChange={(e) => setForm({ ...form, colors: e.target.value })}
            className="h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <input
            placeholder="Finishes (display text)"
            value={form.finishes}
            onChange={(e) => setForm({ ...form, finishes: e.target.value })}
            className="h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
          <input
            placeholder="Video URL"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            className="sm:col-span-2 h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
          />
        </div>
        <textarea
          required
          rows={5}
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-transparent"
        />
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={(e) => setForm({ ...form, featured: e.target.checked })}
          />
          Featured product
        </label>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Specifications</h3>
            <button
              type="button"
              onClick={() => setSpecs([...specs, defaultSpec()])}
              className="text-sm font-semibold text-primary-600"
            >
              + Add row
            </button>
          </div>
          {specs.map((s, i) => (
            <div key={i} className="grid sm:grid-cols-4 gap-2 mb-2">
              <input
                placeholder="Code"
                value={s.code}
                onChange={(e) => {
                  const next = [...specs];
                  next[i] = { ...s, code: e.target.value };
                  setSpecs(next);
                }}
                className="h-10 px-3 rounded-lg border text-sm"
              />
              <input
                placeholder="Material"
                value={s.material}
                onChange={(e) => {
                  const next = [...specs];
                  next[i] = { ...s, material: e.target.value };
                  setSpecs(next);
                }}
                className="h-10 px-3 rounded-lg border text-sm"
              />
              <input
                placeholder="Length"
                value={s.length}
                onChange={(e) => {
                  const next = [...specs];
                  next[i] = { ...s, length: e.target.value };
                  setSpecs(next);
                }}
                className="h-10 px-3 rounded-lg border text-sm"
              />
              <input
                placeholder="Width"
                value={s.width}
                onChange={(e) => {
                  const next = [...specs];
                  next[i] = { ...s, width: e.target.value };
                  setSpecs(next);
                }}
                className="h-10 px-3 rounded-lg border text-sm"
              />
            </div>
          ))}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="h-11 px-6 rounded-xl bcr-btn-primary text-sm font-semibold"
        >
          {loading ? "Saving…" : isEdit ? "Update product" : "Create product"}
        </button>
      </form>

      {isEdit && sku && (
        <>
          <section className="mt-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 space-y-4">
            <h2 className="font-bold">Product images</h2>
            <div className="flex flex-wrap gap-2">
              <input
                placeholder="New color name"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="h-10 px-3 rounded-lg border text-sm"
              />
              <button
                type="button"
                onClick={addColor}
                className="h-10 px-3 rounded-lg border text-sm font-medium"
              >
                Add color
              </button>
              <input
                placeholder="New finish name"
                value={newFinish}
                onChange={(e) => setNewFinish(e.target.value)}
                className="h-10 px-3 rounded-lg border text-sm"
              />
              <button
                type="button"
                onClick={addFinish}
                className="h-10 px-3 rounded-lg border text-sm font-medium"
              >
                Add finish
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input
                placeholder="Image URL"
                value={imgForm.image_url}
                onChange={(e) =>
                  setImgForm({ ...imgForm, image_url: e.target.value })
                }
                className="h-11 px-3 rounded-xl border"
              />
              <select
                value={imgForm.color || ""}
                onChange={(e) =>
                  setImgForm({ ...imgForm, color: Number(e.target.value) })
                }
                className="h-11 px-3 rounded-xl border"
              >
                <option value="">Color</option>
                {colors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={imgForm.finish || ""}
                onChange={(e) =>
                  setImgForm({ ...imgForm, finish: Number(e.target.value) })
                }
                className="h-11 px-3 rounded-xl border"
              >
                <option value="">Finish</option>
                {finishes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <FileUploader
              folder="product_images"
              label="Upload image"
              onUploaded={(url) => setImgForm({ ...imgForm, image_url: url })}
            />
            <button
              type="button"
              onClick={addImage}
              className="h-10 px-4 rounded-xl bcr-btn-primary text-sm font-semibold"
            >
              Attach image
            </button>
            <div className="grid sm:grid-cols-3 gap-3 pt-2">
              {images.map((img) => (
                <div
                  key={img.id}
                  className="rounded-xl border overflow-hidden relative"
                >
                  <img
                    src={img.image_url}
                    alt=""
                    className="w-full h-28 object-cover"
                  />
                  <div className="p-2 text-xs">
                    {img.color_name} / {img.finish_name}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    className="absolute top-2 right-2 bg-white/90 text-red-600 text-xs font-bold px-2 py-1 rounded"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 space-y-4">
            <h2 className="font-bold">PDF attachments</h2>
            <label className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-dashed cursor-pointer text-sm font-medium">
              Upload PDF
              <input
                type="file"
                accept="application/pdf"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onPdfUpload(f);
                  e.target.value = "";
                }}
              />
            </label>
            <ul className="space-y-2">
              {pdfs.map((pdf) => (
                <li
                  key={pdf.slot}
                  className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm"
                >
                  <a
                    href={pdf.url}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate text-primary-600"
                  >
                    Slot {pdf.slot}: {pdf.name}
                  </a>
                  <button
                    type="button"
                    className="text-red-600 text-xs font-semibold"
                    onClick={async () => {
                      const updated = await deleteProductPdf(sku, pdf.slot);
                      setPdfs(updated.pdf_attachments || []);
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
              {pdfs.length === 0 && (
                <li className="text-sm text-neutral-500">No PDFs attached.</li>
              )}
            </ul>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminProductForm;
